/**
 * 固件升级状态与操作。
 */
import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type { FirmwareTab, OtaState } from '@/core/firmware-types'
import { FIRMWARE_TABS } from '@/core/firmware-types'
import {
  buildFirmwareUrl,
  compareFirmwareVersion,
  getDeviceCurrentVersion,
  getFirmwareHistoryList,
  getFirmwareMockData,
  getTabsWithFirmwareUpdate,
  hasAnyFirmwareUpdate,
  hasFirmwareUpdate,
} from '@/core/firmware-mock-data'
import {
  cancelFirmwareUpgrade,
  pickHidDeviceManually,
  runFirmwareUpgrade,
  validateConnectionForTab,
} from '@/core/firmware-upgrade-service'
import { formatBytes } from '@/api/device/firmware'
import { useDeviceStore } from './device'
import { useUserStore } from './user'

export type { FirmwareTab }

export const useFirmwareStore = defineStore('firmware', () => {
  const deviceStore = useDeviceStore()
  const userStore = useUserStore()

  const activeTab = ref<FirmwareTab>('earphone')
  const otaState = ref<OtaState>('idle')
  const isChecking = ref(false)
  const isUpgrading = ref(false)
  const error = ref('')
  const connectionMismatch = ref<{ actualType: 'USB' | '2.4G' | ''; requiredType: 'USB' | '2.4G'; tab: FirmwareTab } | null>(null)
  const statusMessage = ref('')
  const logs = ref<string[]>([])

  const upgradeProgress = ref({
    progress: 0,
    currentBytes: 0,
    totalBytes: 0,
    message: '',
  })

  const selectedVersion = ref('')
  const selectedFileName = ref('')
  const customFirmwareFile = ref<File | null>(null)

  const deviceDetailRecord = computed(() => {
    const d = deviceStore.deviceDetail
    const s = deviceStore.deviceStatus
    if (!d && !s) return null

    return {
      ...(d || {}),
      softwareVersion: d?.softwareVersion ?? s?.softwareVersion,
      bluetoothVersion: d?.bluetoothVersion ?? s?.bluetoothVersion,
      noiseCancelVersion: d?.noiseCancelVersion ?? s?.noiseCancelVersion,
      dongleVersion: d?.dongleVersion ?? s?.dongleVersion,
      headsetColor: s?.headsetColor,
      headsetColorName: s?.headsetColorName,
    } as Record<string, unknown>
  })

  const currentVersion = computed(() =>
    getDeviceCurrentVersion(activeTab.value, deviceDetailRecord.value)
  )
  const mockData = computed(() => getFirmwareMockData(activeTab.value, deviceDetailRecord.value, userStore.uuid))
  const latestVersion = computed(() => mockData.value.latestVersion)
  const hasNewVersion = computed(() =>
    hasFirmwareUpdate(activeTab.value, deviceDetailRecord.value, userStore.uuid)
  )
  const tabsWithUpdate = computed(() =>
    getTabsWithFirmwareUpdate(deviceDetailRecord.value, userStore.uuid)
  )
  const anyUpdate = computed(() => hasAnyFirmwareUpdate(deviceDetailRecord.value, userStore.uuid))
  const historyList = computed(() => getFirmwareHistoryList(activeTab.value, deviceDetailRecord.value, userStore.uuid))

  const progressText = computed(() => {
    const { progress, message } = upgradeProgress.value
    if (otaState.value === 'completed') return 'Upgrade complete'
    if (message) return message
    if (progress === 0) return 'Preparing...'
    if (progress === 100) return 'Upgrade complete'
    return `${progress}%`
  })

  const fileSizeText = computed(() => {
    const { currentBytes, totalBytes } = upgradeProgress.value
    return `${formatBytes(currentBytes)} / ${formatBytes(totalBytes)}`
  })

  function addLog(message: string) {
    logs.value.push(`[${new Date().toLocaleTimeString()}] ${message}`)
    if (logs.value.length > 100) logs.value.shift()
  }

  function setActiveTab(tab: FirmwareTab) {
    if (isUpgrading.value) return
    activeTab.value = tab
    error.value = ''
    connectionMismatch.value = null
    selectedVersion.value = ''
    selectedFileName.value = ''
    customFirmwareFile.value = null
  }

  async function checkUpdate(): Promise<boolean> {
    isChecking.value = true
    error.value = ''
    statusMessage.value = ''

    try {
      await new Promise(resolve => setTimeout(resolve, 300))
      if (!deviceStore.isConnected) {
        error.value = 'Device is not connected'
        return false
      }

      selectedVersion.value = latestVersion.value
      statusMessage.value = hasNewVersion.value
        ? `New version found: ${latestVersion.value}`
        : 'Already on the latest version'
      return hasNewVersion.value
    } finally {
      isChecking.value = false
    }
  }

  async function startUpgrade(version?: string, hidDevice?: HIDDevice | null, firmwareFileName?: string): Promise<void> {
    if (isUpgrading.value) return

    const ver = (version || selectedVersion.value || latestVersion.value).trim()
    const fileName = String(firmwareFileName || selectedFileName.value || mockData.value.latestFileName || '').trim()
    connectionMismatch.value = null
    if (!ver) {
      showFailure('Please select a firmware version')
      return
    }

    if (!navigator.hid) {
      showFailure('Current browser does not support WebHID')
      return
    }

    // 先进入可见的重试/进度状态，再执行异步设备发现；
    // 即使当前没有已授权设备，点击历史版本后页面也应立即有响应。
    isUpgrading.value = true
    otaState.value = 'transferring'
    error.value = ''
    connectionMismatch.value = null
    logs.value = []
    statusMessage.value = 'Preparing upgrade...'
    upgradeProgress.value = {
      progress: 0,
      currentBytes: 0,
      totalBytes: 0,
      message: 'Preparing upgrade...',
    }

    try {
      // 旧项目跳转到独立升级页后，主页轮询会因页面卸载而停止；
      // 当前是单页应用，必须在 OTA 占用 HID 通道前显式暂停轮询。
      await deviceStore.suspendStatusPolling()

      let devices: HIDDevice[]
      try {
        devices = await navigator.hid.getDevices()
      } catch (e) {
        otaState.value = 'failed'
        isUpgrading.value = false
        upgradeProgress.value.progress = 100
        upgradeProgress.value.message = 'Upgrade failed'
        statusMessage.value = 'Upgrade failed'
        error.value = e instanceof Error ? e.message : 'Unable to detect device'
        return
      }
      const conn = validateConnectionForTab(activeTab.value, devices, hidDevice)
      if (!conn.ok) {
        showFailure(conn.message || 'Connection type mismatch')
        connectionMismatch.value = { actualType: conn.actualType, requiredType: conn.requiredType, tab: activeTab.value }
        return
      }

      const totalEstimate = 512 * 1024

      try {
        await runFirmwareUpgrade(
          activeTab.value,
          ver,
          {
            onProgress: (percent, message) => {
              upgradeProgress.value = {
                progress: percent,
                currentBytes: Math.floor((percent / 100) * totalEstimate),
                totalBytes: totalEstimate,
                message,
              }
              statusMessage.value = message
            },
            onComplete: () => {
              otaState.value = 'completed'
              isUpgrading.value = false
              upgradeProgress.value.progress = 100
              statusMessage.value = 'Upgrade complete'
              addLog('Upgrade succeeded')
            },
            onError: err => {
              otaState.value = 'failed'
              isUpgrading.value = false
              upgradeProgress.value.progress = 100
              upgradeProgress.value.message = 'Upgrade failed'
              statusMessage.value = 'Upgrade failed'
              error.value = err.message
              addLog(`Upgrade failed: ${err.message}`)
            },
            onLog: (msg, level) => addLog(`[${level || 'info'}] ${msg}`),
          },
          {
            firmwareFile: customFirmwareFile.value || undefined,
            firmwareUrl: customFirmwareFile.value ? undefined : buildFirmwareUrl(activeTab.value, ver, fileName),
            hidDevice,
          }
        )
      } catch (e) {
        otaState.value = 'failed'
        isUpgrading.value = false
        upgradeProgress.value.progress = 100
        upgradeProgress.value.message = 'Upgrade failed'
        statusMessage.value = 'Upgrade failed'
        error.value = e instanceof Error ? e.message : 'Upgrade failed'
      }
    } finally {
      // 失败/重试页面仍打开时保持主页轮询暂停，否则会再次与 SBT OTA 通道竞争；
      // 升级成功后可在自动跳转主页前恢复轮询。
      if (String(otaState.value) === 'completed') deviceStore.resumeStatusPolling()
    }
  }

  async function startUpgradeFromFile(file: File): Promise<void> {
    customFirmwareFile.value = file
    selectedVersion.value = file.name.replace(/\.[^.]+$/, '')
    selectedFileName.value = ''
    await startUpgrade(selectedVersion.value)
  }

  async function manualPickDevice(): Promise<HIDDevice | null> {
    try {
      const dev = await pickHidDeviceManually()
      if (!dev) {
        error.value = 'No device selected'
        return null
      }
      if (!dev.opened) await dev.open()
      addLog(`Manually selected: ${dev.productName || 'Unknown device'}`)
      return dev
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Manual device selection failed'
      return null
    }
  }

  function cancelUpgrade() {
    cancelFirmwareUpgrade()
    deviceStore.resumeStatusPolling()
    isUpgrading.value = false
    otaState.value = 'idle'
    upgradeProgress.value = { progress: 0, currentBytes: 0, totalBytes: 0, message: '' }
    statusMessage.value = 'Cancelled'
  }

  function clearLogs() {
    logs.value = []
  }

  function reset() {
    cancelUpgrade()
    error.value = ''
    connectionMismatch.value = null
    statusMessage.value = ''
    selectedVersion.value = ''
    selectedFileName.value = ''
    customFirmwareFile.value = null
    otaState.value = 'idle'
  }

  function selectHistoryVersion(version: string, fileName = '') {
    selectedVersion.value = version
    selectedFileName.value = fileName
  }

  /** 在没有升级目标直接打开页面时，保持独立升级页可用；
   * 该状态也用于升级中断或失败后的恢复页面。 */
  function showFailure(message = '') {
    cancelFirmwareUpgrade()
    isUpgrading.value = false
    otaState.value = 'failed'
    error.value = message
    connectionMismatch.value = null
    statusMessage.value = 'Upgrade failed'
    upgradeProgress.value = {
      progress: 100,
      currentBytes: 0,
      totalBytes: 0,
      message: 'Upgrade failed',
    }
  }

  async function retryWithManualDevice(
    version = selectedVersion.value || latestVersion.value,
    firmwareFileName = selectedFileName.value
  ): Promise<void> {
    const device = await manualPickDevice()
    if (!device) return
    await startUpgrade(version, device, firmwareFileName)
  }

  return {
    FIRMWARE_TABS,
    activeTab,
    otaState,
    isChecking,
    isUpgrading,
    error,
    connectionMismatch,
    statusMessage,
    logs,
    upgradeProgress,
    selectedVersion,
    selectedFileName,
    customFirmwareFile,
    currentVersion,
    latestVersion,
    mockData,
    hasNewVersion,
    tabsWithUpdate,
    anyUpdate,
    historyList,
    progressText,
    fileSizeText,
    compareFirmwareVersion,
    setActiveTab,
    checkUpdate,
    startUpgrade,
    startUpgradeFromFile,
    manualPickDevice,
    cancelUpgrade,
    clearLogs,
    reset,
    selectHistoryVersion,
    showFailure,
    retryWithManualDevice,
    buildFirmwareUrl,
  }
})
